import { Component, Prop, h, Host } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'call-to-action-type-2';

const ITEM_ICONS = [
  'https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-1.svg',
  'https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-2.svg',
  'https://marketing-static.signnow.com/347/images/me/cta-type-2/icon-3.svg',
];

/**
 * Call-to-action component with two-column layout: text + feature list.
 * Left column: label, heading, and action buttons.
 * Right column (desktop only): up to 3 feature list items with icons.
 *
 * @product signnow
 * @version 2.0.0
 * @category SignNow
 * @contentConfig Call-to-action component for encouraging user engagement and conversions
 * @slot label - <me-text tag="span"> - Small uppercase label shown above the heading
 * @slot heading - {required}, <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Main CTA heading
 * @slot action-primary - {required}, <me-button variant="primary"> - Primary action button
 * @slot action-secondary - <me-button variant="bordered"> - Secondary action button
 * @example
 *   <sn-call-to-action-type-1
 *     items='[{"title":"Best ROI.","description":"Our customers achieve an average 7x ROI within the first six months."},{"title":"Scales with your use cases.","description":"From SMBs to mid-market, airSlate SignNow delivers results for businesses of all sizes."},{"title":"Intuitive UI and API.","description":"Sign and send documents from your apps in minutes."}]'
 *   >
 *     <me-text slot="label" tag="span" class="me-text--label">be ready to get more</me-text>
 *     <me-heading slot="heading" variant="2" tag="h2">Get legally-binding signatures now!</me-heading>
 *     <me-button slot="action-primary" variant="primary" href="https://snseats.signnow.com/purchase/business_free_trial/loggedout">Start free trial</me-button>
 *     <me-button slot="action-secondary" variant="bordered" href="/contact">Request a call</me-button>
 *   </sn-call-to-action-type-1>
 */
@Component({
  tag: 'sn-call-to-action-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNCallToActionType1 {
  /**
   * Color mode for theming.
   */
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Feature list items shown in the right column (desktop only).
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Bold feature title
   * @item.property {string} description - Feature description text
   * @item.required title
   * @item.content title description
   * @minItems 1
   * @maxItems 3
   */
  @Prop({ mutable: true }) items?: any[] | string;

  private parsedItems: Array<{ title: string; description?: string }> = [];

  componentWillLoad() {
    const parsed = parseJsonProp(this.items, []);
    this.parsedItems = Array.isArray(parsed) ? parsed : [];
  }

  render() {
    const items = this.parsedItems;

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={`${COMPONENT_NAME} sn-call-to-action-type-1`} yGap="2xl" yGapLg="5xl" role="region" aria-label="Call to Action">
          <div class={`${COMPONENT_NAME}__content`}>
            <me-row yGap="3xl" yGapLg="none" xGap="none" alignLg="center">
            <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__intro`}>
              <div class={`${COMPONENT_NAME}__intro-inner`}>
                <slot name="label"></slot>
                <slot name="heading"></slot>
                <me-row xGap="md" xGapLg="md" yGap="md" yGapLg="md" justify="center" justifyLg="start" class={`${COMPONENT_NAME}__actions`}>
                  <me-column span="auto">
                    <slot name="action-primary"></slot>
                  </me-column>
                  <me-column span="auto">
                    <slot name="action-secondary"></slot>
                  </me-column>
                </me-row>
              </div>
            </me-column>
            <me-column span="12" spanLg="6" class={`${COMPONENT_NAME}__media`}>
              <div class={`${COMPONENT_NAME}__media-inner`}>
                {items.length > 0 && (
                  <ul class="cta-list">
                    {items.slice(0, 3).map((item, i) => (
                      <li class="cta-list__item">
                        <div class="cta-list__logo">
                          <me-icon name={ITEM_ICONS[i]} />
                        </div>
                        <div class="cta-list__description">
                          <strong>{item.title}</strong>{item.description ? ` ${item.description}` : ''}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </me-column>
          </me-row>
          </div>
        </me-section>
      </Host>
    );
  }
}
